// The private weekly recap, composed editorial-journal style. Per the M2.4
// design doc, this is an in-app reflection moment — no share button, no
// notifications, no public surface. Rendered on Profile.
//
// The "Sunday-evening prominence" the doc describes is implemented by a
// quiet visual flag in the corner — no modal, no banner, just a typographic
// shift so the recap reads as "this is the moment to look back."

import React from "react";
import { formatXp } from "../lib/levels";
import type { WeeklyRecap } from "../lib/api-client";

interface Props {
  recap: WeeklyRecap;
  weekStart: string;
  weekTarget: number;
  practiceDaysThisWeek: number;
  todayKey: string;
}

function isSundayOrLater(weekStart: string, todayKey: string): boolean {
  // weekStart is Monday. Sunday is start + 6 days.
  const [y, m, d] = weekStart.split("-").map(Number);
  const sunday = new Date(Date.UTC(y, m - 1, d + 6))
    .toISOString()
    .slice(0, 10);
  return todayKey >= sunday;
}

function Tally({
  label,
  count,
  xp,
}: {
  label: string;
  count: number;
  xp: number;
}) {
  return (
    <div className="text-center">
      <div className="font-display text-3xl text-gray-100 tabular-nums">
        {formatXp(count)}
      </div>
      <div className="mt-0.5 font-display text-[10px] tracking-[0.22em] uppercase text-gray-500">
        {label}
      </div>
      {xp > 0 ? (
        <div className="mt-0.5 font-display-italic text-[11px] text-orange-200/70">
          +{formatXp(xp)} XP
        </div>
      ) : null}
    </div>
  );
}

export default function WeeklyRecapBlock({
  recap,
  weekStart,
  weekTarget,
  practiceDaysThisWeek,
  todayKey,
}: Props) {
  const isRecapMoment = isSundayOrLater(weekStart, todayKey);
  const totalCount =
    recap.objectivesCheckedThisWeek +
    recap.videosCompletedThisWeek +
    recap.firstOfDayThisWeek +
    recap.bookmarksThisWeek;
  const targetMet = practiceDaysThisWeek >= weekTarget;

  // Mid-week pep / weekend reflection — same composition, different copy.
  const headline = (() => {
    if (totalCount === 0) {
      return isRecapMoment
        ? "the week looked back at you"
        : "the week is still ahead";
    }
    if (targetMet) {
      return isRecapMoment
        ? "a week kept, end to end"
        : "the week is already in good shape";
    }
    return isRecapMoment
      ? "what the week held"
      : "what the week holds so far";
  })();

  // Format the Mon-Sun range as e.g. "May 25 — May 31". Format in UTC so
  // the local browser TZ doesn't pull the date-key (TZ-naive) back a day.
  const range = (() => {
    const [y, m, d] = weekStart.split("-").map(Number);
    const start = new Date(Date.UTC(y, m - 1, d));
    const end = new Date(Date.UTC(y, m - 1, d + 6));
    const fmt = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    return `${fmt.format(start)} — ${fmt.format(end)}`;
  })();

  return (
    <section
      className="relative mx-auto max-w-md"
      data-testid="weekly-recap"
    >
      {/* Decorative corner ornament when it's the recap moment. */}
      {isRecapMoment ? (
        <span
          aria-hidden
          className="absolute -top-3 right-0 font-display-italic text-[10px] tracking-[0.3em] uppercase text-orange-300/70"
        >
          recap
        </span>
      ) : null}

      <p className="text-center font-display text-gray-500 text-[10px] tracking-[0.35em] uppercase mb-2">
        {range}
      </p>
      <h2 className="text-center font-display-italic text-gray-200 text-xl sm:text-2xl">
        {headline}
      </h2>

      {/* Big XP-this-week number. */}
      <div className="mt-6 text-center">
        <div className="font-display text-5xl text-white tabular-nums">
          {formatXp(recap.xpThisWeek)}
        </div>
        <div className="mt-1 font-display text-[10px] tracking-[0.3em] uppercase text-gray-500">
          XP this week
        </div>
      </div>

      {/* Tally grid — 2×2 on small, single row on wider. */}
      {totalCount > 0 ? (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-y-6 gap-x-4">
          {recap.objectivesCheckedThisWeek > 0 ? (
            <Tally
              label="objectives"
              count={recap.objectivesCheckedThisWeek}
              xp={recap.objectivesCheckedThisWeek * 10}
            />
          ) : null}
          {recap.videosCompletedThisWeek > 0 ? (
            <Tally
              label="videos"
              count={recap.videosCompletedThisWeek}
              xp={recap.videosCompletedThisWeek * 50}
            />
          ) : null}
          {recap.firstOfDayThisWeek > 0 ? (
            <Tally
              label="days started"
              count={recap.firstOfDayThisWeek}
              xp={recap.firstOfDayThisWeek * 20}
            />
          ) : null}
          {recap.bookmarksThisWeek > 0 ? (
            <Tally
              label="bookmarks"
              count={recap.bookmarksThisWeek}
              xp={recap.bookmarkXpThisWeek}
            />
          ) : null}
        </div>
      ) : (
        <p className="mt-6 text-center font-display-italic text-gray-500">
          nothing on the page yet — pick up a guitar
        </p>
      )}

      {/* Target status line. */}
      <p
        className={`mt-8 text-center font-display-italic text-sm ${
          targetMet ? "text-orange-200" : "text-gray-400"
        }`}
      >
        {targetMet
          ? `${practiceDaysThisWeek} of ${weekTarget} days — week target met`
          : `${practiceDaysThisWeek} of ${weekTarget} days toward the week`}
      </p>
    </section>
  );
}
